import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';

const BLAST_INCLUDES = [
  'assignment.assignment-user-metas.user',
  'assignment.applications.actions.actionable',
  'assignment.applications.seller',
  'assignment.applications.thread',
  'blast-users.user',
  'blast-users.user.location',
  'blast-users.user.last-location',
];

const BLAST_INCLUDES_STRING = BLAST_INCLUDES.join(',');

class AdminBlasterController extends Controller {
  queryParams = ['section'];

  @service('commands/blast-commands') blastCommandService;
  @service shData;
  @service('states/routes/admin/blaster') state;
  @service('state-managers/routes/admin/blaster') stateManager;

  section = null;
  historyFilterBlast = null;
  historyFilterStatus = null;
  blastHistory = [];

  /** @type {Assignment} */
  @readOnly('state.currentAssignment') posting;

  /** @type {boolean} */
  @readOnly('state.isLoadingBlastResults') isLoadingBlastResults;

  /** @type {boolean} */
  @readOnly('loadBlastHistory.isRunning') isLoadingBlastHistory;

  /**
   * @returns {Array<User>}
   */
  @computed('blastHistoryUsers.@each.userWasBlasted')
  get blastedUsers() {
    const { blastHistoryUsers } = this;

    return blastHistoryUsers.reduce((accum, blastHistoryUser) => {
      if (blastHistoryUser.userWasBlasted) {
        accum.push(blastHistoryUser.user);
      }

      return accum;
    }, []);
  }

  @computed('blastedUserIdToApplicationMap')
  get blastedUsersApplications() {
    return Object.values(this.blastedUserIdToApplicationMap);
  }

  /**
   * A list of BlastUsers from the combined blast history.
   * A BlastUser will appear multiple times for each time they were blasted.
   * 
   * @returns {Array<BlastUser>}
   */
  @computed('blastHistory.[]')
  get blastHistoryUsers() {
    return this.blastHistory.reduce((accum, { blastUsers }) => {
      accum = accum.concat(blastUsers.toArray());
      return accum;
    }, []);
  }

  @computed('blastedUsers.[]','sellerIdToApplicationMap')
  get blastedUserIdToApplicationMap() {
    const { sellerIdToApplicationMap } = this;

    return this.blastedUsers.reduce((accum, { id }) => {
      const application = sellerIdToApplicationMap[id];

      // only add newest application
      if (
        application
        && (
          !accum[id]
          || new Date(application.createdAt) > new Date(accum[id].createdAt)
        )
      ) {
        accum[id] = application;
      }

      return accum;
    }, {});
  }

  @computed('allApplications.[]')
  get sellerIdToApplicationMap() {
    return this.allApplications.reduce((accum, application) => {
      accum[application.seller.id] = application;

      return accum;
    }, {});
  }

  @computed('blastHistory.[]')
  get allApplications() {
    return this.blastHistory.reduce((accum, { assignment }) => {
      accum = accum.concat(assignment.applications.toArray());
      return accum;
    }, []);
  }

  /**
   * Called when the user navigates between tabs.
   *
   * @param {String} section
   */
  @action
  changeSection(section) {
    this.set('section', section);
  }

  /**
   * Called when the user filters history by a blast.
   *
   * @param {String} blast
   */
  @action
  historyChangeFilterBlast(blast) {
    this.set('historyFilterBlast', blast);
  }

  /**
   * Called when the user filters history by a status.
   *
   * @param {String} blast
   */
  @action
  historyChangeFilterStatus(status) {
    this.set('historyFilterStatus', status);
  }

  @dropTask
  *loadBlastHistory(assignment) {
    const options = {
      params: {
        include: BLAST_INCLUDES_STRING,
      }
    };

    try {
      const response = yield this.blastCommandService.loadBlastHistory(assignment, options);
      this.shData.pushResponseToStore(response);
      this.set('blastHistory', this.shData.getModelArrayFromResponse(response, 'blast'));
    } catch(e) {
      throw e;
    }
  }
}

export default AdminBlasterController;
